import { httpsCallable } from 'firebase/functions';
import { brandConfig } from 'src/config';
import { AUTH, CLOUD_FUNCTIONS } from '../../auth/firebaseApp';
import { cloud_afterSignIn } from './cloudFunctions';
import { sendToSentry, sentryOOBActionsFingerprint } from './sentryReporter';

/**
 * Brand codes accepted by the afterSignIn callable.
 * Same source as upsertUserCredential / getSignInMethods / updateUserBrand.
 */
export type AfterSignInBrand = 'MRR' | 'BVR';

export function getAfterSignInBrand(): AfterSignInBrand {
  return brandConfig.brandCode as AfterSignInBrand;
}

/**
 * Records a successful Firebase Auth sign-in via the afterSignIn callable.
 *
 * Call only after auth succeeds (AUTH.currentUser must exist). Safe to call
 * repeatedly: refreshes lastSignInTime every time; adds brand to brands[] only if missing.
 * Do not pass email — the backend derives it from the authenticated Firebase user.
 *
 * Note: phone-only users without a Firebase Auth email may fail server-side;
 * the backend currently requires email. Failures are logged and do not block sign-in.
 */
export async function recordSuccessfulSignIn(brand: AfterSignInBrand): Promise<void> {
  if (!AUTH.currentUser) {
    console.warn('recordSuccessfulSignIn skipped: no authenticated user');
    return;
  }

  try {
    const afterSignIn = httpsCallable<{ brand: AfterSignInBrand }, { success: true }>(
      CLOUD_FUNCTIONS,
      cloud_afterSignIn
    );
    await afterSignIn({ brand });
  } catch (error) {
    // Sign-in already succeeded; never block navigation on afterSignIn failure.
    console.error('afterSignIn failed:', error);
    sendToSentry(error, 'afterSignIn failed', sentryOOBActionsFingerprint, cloud_afterSignIn, {
      brand
    });
  }
}

/**
 * Runs a Firebase sign-in function, then records the successful sign-in.
 * Use this wrapper on every auth completion path so afterSignIn is never skipped.
 *
 * Post-sign-in recording happens here (via recordSuccessfulSignIn) after signInFn resolves.
 */
export async function completeSignIn<T>({
  brand,
  signInFn
}: {
  brand: AfterSignInBrand;
  signInFn: () => Promise<T>;
}): Promise<T> {
  const result = await signInFn();
  await recordSuccessfulSignIn(brand);
  return result;
}
